// « Signaler un problème » (04/11/2026) : administrateur et comptable, sur toutes les
// pages (barre du haut). Message libre ; la page et la date sont enregistrées
// automatiquement ; capture d'écran facultative (fichier ou collée avec Ctrl+V).
// Aucune donnée comptable n'est jointe automatiquement.
import { useEffect, useState } from "react";
import { MessageSquareWarning } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import api from "../services/api";

const MAX_SCREENSHOT = 5 * 1024 * 1024;
const TYPES = ["image/png", "image/jpeg", "image/webp"];
const toBase64 = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(String(r.result).split(",")[1] || "");
  r.onerror = reject;
  r.readAsDataURL(file);
});

export default function ProblemReportButton({ currentPage, pageLabel }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [shot, setShot] = useState(null); // { file, url }
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(null);
  useEffect(() => () => { if (shot?.url) URL.revokeObjectURL(shot.url); }, [shot]);
  if (!["admin", "comptable"].includes(user?.role)) return null;

  const close = () => { setOpen(false); setMessage(""); setShot(null); setError(""); setDone(null); };
  const takeImage = (file) => {
    if (!file) return;
    if (!TYPES.includes(file.type)) { setError("Capture d'écran : image PNG, JPEG ou WebP seulement."); return; }
    if (file.size > MAX_SCREENSHOT) { setError("Capture d'écran trop lourde (5 Mo au plus)."); return; }
    setError(""); setShot({ file, url: URL.createObjectURL(file) });
  };
  const onPaste = (e) => {
    const item = [...(e.clipboardData?.items || [])].find(i => i.kind === "file" && TYPES.includes(i.type));
    if (item) { e.preventDefault(); takeImage(item.getAsFile()); }
  };
  const send = async () => {
    setBusy(true); setError("");
    try {
      const screenshot = shot ? { mime: shot.file.type, content_base64: await toBase64(shot.file) } : undefined;
      const r = await api.problemReports.create({ message, page: currentPage || "", page_label: pageLabel || "", ...(screenshot ? { screenshot } : {}) });
      setDone(r);
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  return (
    <>
      <button onClick={() => setOpen(true)} title="Signaler un problème à l'équipe ExecPro360"
        style={{ display: "flex", alignItems: "center", gap: 5, background: "var(--bg-secondary)", border: "var(--border)", borderRadius: 8, padding: "5px 10px", fontSize: 12, color: "var(--text-secondary)", cursor: "pointer", flexShrink: 0 }}>
        <MessageSquareWarning size={13} />
        <span>Signaler un problème</span>
      </button>
      {open && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && close()}>
          <div className="modal" style={{ maxWidth: 560, width: "94%" }} onPaste={onPaste}>
            <div className="modal-header"><h3 className="modal-title">Signaler un problème</h3><button className="modal-close" onClick={close} aria-label="Fermer">✕</button></div>
            {done ? (
              <div style={{ fontSize: 13 }}>
                <div style={{ background: "#f0fdf4", color: "#166534", borderRadius: 8, padding: "8px 12px", marginBottom: 10 }}>
                  ✓ Signalement n° {done.id} envoyé à l'équipe ExecPro360. Nous revenons vers vous rapidement.
                </div>
                <div style={{ textAlign: "right" }}><button className="btn" onClick={close}>Fermer</button></div>
              </div>
            ) : (
              <>
                <div style={{ fontSize: 12.5, color: "var(--text-secondary)", marginBottom: 8 }}>
                  Décrivez ce qui ne va pas : ce que vous faisiez, ce que vous attendiez, ce qui s'est passé.
                  La page (<strong>{pageLabel || currentPage}</strong>) et la date sont ajoutées automatiquement.
                </div>
                <textarea className="input" rows={6} maxLength={4000} value={message} onChange={e => setMessage(e.target.value)} autoFocus
                  placeholder="Ex. : la facture du 29/09 ne se valide pas, le bouton ne fait rien."
                  style={{ width: "100%", resize: "vertical", fontSize: 13 }} />
                <div style={{ marginTop: 10, fontSize: 12.5 }}>
                  <strong>Capture d'écran</strong> (facultative) : choisissez une image, ou collez-la ici (Ctrl+V).
                  <div style={{ marginTop: 6, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                    <input type="file" accept={TYPES.join(",")} disabled={busy} onChange={e => { takeImage(e.target.files?.[0]); e.target.value = ""; }} />
                    {shot && <button className="btn" style={{ fontSize: 11, padding: "2px 8px" }} onClick={() => setShot(null)}>Retirer la capture</button>}
                  </div>
                  {shot && <img src={shot.url} alt="Capture d'écran jointe" style={{ marginTop: 8, maxWidth: "100%", maxHeight: 180, border: "1px solid #e5e7eb", borderRadius: 6 }} />}
                </div>
                <div style={{ marginTop: 10, fontSize: 11.5, color: "var(--text-muted)" }}>
                  Aucune donnée comptable n'est jointe : seuls votre message, la page, la date et, si vous l'ajoutez, la capture d'écran sont transmis.
                  Une capture peut montrer des montants ou des noms : n'en joignez une que si elle est utile.
                </div>
                {error && <div style={{ marginTop: 8, background: "#fee2e2", color: "#991b1b", borderRadius: 8, padding: "6px 10px", fontSize: 12.5 }}>{error}</div>}
                <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 12 }}>
                  <button className="btn" onClick={close}>Annuler</button>
                  <button className="btn primary" disabled={busy || !message.trim()} onClick={send}>{busy ? "Envoi…" : "Envoyer"}</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
